import { useEffect, useRef, useCallback, createContext, useContext, useState } from 'react';
import { useAuth } from '../context/AuthContext';

const WebSocketContext = createContext(null);

const WS_PING_INTERVAL = 25000;
const WS_RECONNECT_BASE = 2000;
const WS_RECONNECT_MAX = 30000;
const WS_MAX_RECONNECT_ATTEMPTS = 10;

export function WebSocketProvider({ children }) {
  const { user, accessToken } = useAuth();
  const wsRef = useRef(null);
  const listenersRef = useRef(new Set());
  const pingTimerRef = useRef(null);
  const reconnectAttemptRef = useRef(0);
  const reconnectTimerRef = useRef(null);
  const intentionalCloseRef = useRef(false);
  const [connected, setConnected] = useState(false);

  const dispatch = useCallback((type, data) => {
    listenersRef.current.forEach(fn => {
      try { fn(type, data); } catch (e) { console.error('[WS] listener error:', e); }
    });
  }, []);

  const cleanup = useCallback(() => {
    clearInterval(pingTimerRef.current);
    pingTimerRef.current = null;
  }, []);

  const attemptReconnectRef = useRef(null);

  const connect = useCallback((token) => {
    if (!token) return;

    const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const host = window.location.host;
    const url = `${proto}//${host}/ws`;

    try {
      const ws = new WebSocket(url);
      wsRef.current = ws;

      ws.onopen = () => {
        ws.send(JSON.stringify({ type: 'auth', token }));
        // connected stays false until auth_success arrives — TCP open is not
        // the same as authenticated, and the UI should not lie about it.
      };

      ws.onmessage = (event) => {
        try {
          const msg = JSON.parse(event.data);
          if (msg.type === 'auth_success') {
            reconnectAttemptRef.current = 0;
            setConnected(true);
            pingTimerRef.current = setInterval(() => {
              if (ws.readyState === WebSocket.OPEN) {
                ws.send(JSON.stringify({ type: 'ping' }));
              }
            }, WS_PING_INTERVAL);
            return;
          }
          if (msg.type === 'auth_error') {
            setConnected(false);
            intentionalCloseRef.current = true; // do not reconnect on auth failure
            ws.close();
            return;
          }
          if (msg.type === 'pong' || msg.type === 'connected') return;
          dispatch(msg.type, msg.data || {});
        } catch { /* ignore malformed frames */ }
      };

      ws.onclose = () => {
        cleanup();
        setConnected(false);
        if (!intentionalCloseRef.current && attemptReconnectRef.current) {
          attemptReconnectRef.current();
        }
      };

      ws.onerror = () => {
        ws.close();
      };
    } catch { /* WebSocket constructor can throw on bad URLs */ }
  }, [dispatch, cleanup]);

  const attemptReconnect = useCallback(() => {
    if (reconnectAttemptRef.current >= WS_MAX_RECONNECT_ATTEMPTS) return;
    reconnectAttemptRef.current += 1;
    const delay = Math.min(
      WS_RECONNECT_BASE * Math.pow(2, reconnectAttemptRef.current - 1),
      WS_RECONNECT_MAX
    );
    reconnectTimerRef.current = setTimeout(() => {
      if (!intentionalCloseRef.current) {
        const token = localStorage.getItem('accessToken');
        connect(token);
      }
    }, delay);
  }, [connect]);

  useEffect(() => {
    attemptReconnectRef.current = attemptReconnect;
  }, [attemptReconnect]);

  const disconnect = useCallback(() => {
    intentionalCloseRef.current = true;
    clearTimeout(reconnectTimerRef.current);
    cleanup();
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
    setConnected(false);
  }, [cleanup]);

  // Drive connection lifecycle from AuthContext, not localStorage. This
  // fires synchronously on same-tab login/logout — the storage event does
  // not, so it can't be the only signal.
  useEffect(() => {
    if (user && accessToken) {
      intentionalCloseRef.current = false;
      reconnectAttemptRef.current = 0;
      connect(accessToken);
      return () => disconnect();
    }
    disconnect();
  }, [user, accessToken, connect, disconnect]);

  // Cross-tab sync: storage events fire in *other* tabs when localStorage
  // changes. Use this only to react to logins/logouts in another tab.
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === 'accessToken') {
        if (!e.newValue) disconnect();
        // login in other tab: AuthContext in this tab will eventually fetch
        // /auth/me and the user-driven effect above will reconnect. We do
        // not connect from the storage event because we don't have a user
        // object yet.
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [disconnect]);

  const subscribe = useCallback((fn) => {
    listenersRef.current.add(fn);
    return () => listenersRef.current.delete(fn);
  }, []);

  return (
    <WebSocketContext.Provider value={{ subscribe, connected }}>
      {children}
    </WebSocketContext.Provider>
  );
}

export function useWS(eventTypes, handler) {
  const ctx = useContext(WebSocketContext);
  const handlerRef = useRef(handler);
  handlerRef.current = handler;

  useEffect(() => {
    if (!ctx) return;
    const types = Array.isArray(eventTypes) ? eventTypes : [eventTypes];
    const listener = (type, data) => {
      if (types.includes(type)) {
        handlerRef.current(type, data);
      }
    };
    return ctx.subscribe(listener);
  }, [ctx, eventTypes]);
}

export function useWSConnected() {
  const ctx = useContext(WebSocketContext);
  return ctx?.connected ?? false;
}
